import type { APIData } from '../../../core/types/APIData';
import type { BattleTest } from '../../../core/types/Battle';
import { QueryParams } from '../../../utils/encodeQueryString';
import { getSearchAPIEndpoint } from '../../../utils/getSearchAPIEndpoint';
import { ApiResponse, GetJSON } from '../apiUtils';

import { ApiDeal } from './createWidgetResponseGetter';

export type ModelPhpGroupEntry = {
  widget: {
    data: APIData;
  };
  models: Record<string, number>;
  is_tenancy?: boolean;
  requested_model?: string;
};

type ModelPhpGroups = ModelPhpGroupEntry[];

export type NormalizedModelPhpGroup = {
  key: string;
  group: ModelPhpGroupEntry;
};

export const normalizeModelPhpGroups = (groups: ModelPhpGroups): NormalizedModelPhpGroup[] => {
  return groups.map((group, index) => ({
    key: group.requested_model?.trim() || group.widget.data.title.trim() || String(index),
    group,
  }));
};

export type ModelPhpResponse = {
  groups: ModelPhpGroups;
  battle?: BattleTest | null;
  area?: string | null;
  site?: string;
  language?: string;
  version?: string;
};

type ModelPhpApiGroupEntry = Omit<ModelPhpGroupEntry, 'widget'> & {
  widget: { data: Omit<APIData, 'offers'> & { offers: ApiDeal[] } };
};

type ModelPhpApiResponse = Omit<ModelPhpResponse, 'groups'> & {
  groups: ModelPhpApiGroupEntry[];
};

const modelPhpApiResponseToModelPhpResponse = (
  response: ApiResponse<ModelPhpApiResponse>,
): ApiResponse<ModelPhpResponse> => {
  if (response.ok) {
    return {
      ...response,
      data: {
        ...response.data,
        groups: response.data.groups.map((group) => ({
          ...group,
          widget: {
            ...group.widget,
            data: {
              ...group.widget.data,
              offers: group.widget.data.offers.map((offer) => ({
                ...offer,
                promos: offer.promos?.map((promo) => ({
                  ...promo,
                  value: String(promo.value),
                })),
              })),
            },
          },
        })),
      },
    };
  }
  return response;
};

export const createModelOffersResponseGetter = (getJSON: GetJSON<ModelPhpApiResponse>) => {
  return async (params?: QueryParams): Promise<ApiResponse<ModelPhpResponse>> => {
    const response = await getJSON(`${getSearchAPIEndpoint()}/model.php`, params);
    return modelPhpApiResponseToModelPhpResponse(response);
  };
};

export type ModelOffersResponseGetter = ReturnType<typeof createModelOffersResponseGetter>;
